// Pure helpers for the client-analytics pipeline (UserEvent): derive a label for
// a clicked element, and validate/sanitize raw client events before they're
// stored. Kept pure + framework-free so the rules (length caps, query-string
// stripping) are unit-tested without a browser or DB.

/**
 * The learning path's own events (docs/learning-path-plan.md, PR 5). `label`
 * carries the canonical step topic. Three are sent by the lesson page and the
 * /learn cards; `checkpoint_pass` is written SERVER-side when a topic session
 * ends with the step passed (src/lib/learn/path-events.ts) — a pass the client
 * could report itself would be worth nothing.
 */
export const PATH_EVENT_TYPES = [
  'path_step_view',
  'lesson_complete',
  'checkpoint_start',
  'checkpoint_pass',
] as const
export type PathEventType = (typeof PATH_EVENT_TYPES)[number]

/**
 * The "How long do you have?" picker on the Today card (time-boxed sessions,
 * QA finding 8). The box rides in `label` so the funnel is readable from the
 * rows alone: `session_preview` = "20" (a box was opened), `session_start` =
 * sessionStartLabel() (Start was pressed on that box's plan).
 */
export const SESSION_EVENT_TYPES = ['session_preview', 'session_start'] as const
export type SessionEventType = (typeof SESSION_EVENT_TYPES)[number]

/**
 * A pick on the four-zone dashboard's Today card — the primary or one of its
 * chips (todayPickLabel). Its own type, not a `click`: the root tracker
 * already records every click as `click`, so reusing that type would count
 * each Today pick twice and blur the click totals the admin pages read.
 */
export const TODAY_EVENT_TYPES = ['today_pick'] as const
export type TodayEventType = (typeof TODAY_EVENT_TYPES)[number]

export const USER_EVENT_TYPES = [
  'pageview',
  'click',
  ...PATH_EVENT_TYPES,
  ...SESSION_EVENT_TYPES,
  ...TODAY_EVENT_TYPES,
] as const
export type UserEventType = (typeof USER_EVENT_TYPES)[number]

/**
 * `session_start`'s label. A full plan (Phase 2) is just its box, "20". A plan
 * built for a student with no starting level says which kind it was, because
 * the primary metric is Checkpoint completion among the students who pick a
 * box: "first-step:20" offered Checkpoint parts, "stopgap:10" offered due
 * reviews and Foundations because no part fit (the Checkpoint is still their
 * first step).
 */
export function sessionStartLabel(
  mode: 'first-step' | 'stopgap' | 'full',
  minutes: number,
): string {
  return mode === 'full' ? String(minutes) : `${mode}:${minutes}`
}

/**
 * `today_pick`'s label: which slot, and where it goes — "primary:/checkpoint",
 * "alternative:/review". The route only: a query string can carry a topic or
 * an assignment flag and isn't what's being measured. An in-page anchor
 * ("#daily-challenge") has no route, so it is kept as it is.
 */
export function todayPickLabel(slot: 'primary' | 'alternative', href: string): string {
  const where = href.startsWith('#') ? href : sanitizePath(href)
  return `${slot}:${where || '/'}`.slice(0, MAX_LABEL_LEN)
}

/** Caps — bound payload size + table growth, and stop pasted junk. */
export const MAX_EVENTS_PER_BATCH = 50
export const MAX_PATH_LEN = 512
export const MAX_LABEL_LEN = 120
export const MAX_ANON_ID_LEN = 64

export type CleanEvent = { type: UserEventType; path: string; label: string | null }

/** Minimal shape of a clicked element we read for a label (DOM or a test stub). */
export type ClickableLike = {
  getAttribute?: (name: string) => string | null
  textContent?: string | null
  tagName?: string
}

/**
 * Human-readable label for a clicked element: an explicit `data-track` wins,
 * then `aria-label`, then trimmed visible text, then the tag name. Whitespace is
 * collapsed and the result is capped so a long button never bloats a row.
 */
export function deriveClickLabel(el: ClickableLike | null | undefined): string {
  if (!el) return 'element'
  const track = el.getAttribute?.('data-track') ?? null
  const aria = el.getAttribute?.('aria-label') ?? null
  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  const raw = track || aria || text || (el.tagName ? el.tagName.toLowerCase() : 'element')
  return raw.slice(0, MAX_LABEL_LEN)
}

/**
 * Strip the query string + hash from a path before storage. These can carry
 * tokens / PII (callbackUrl, reset tokens, emails) and we only want the route.
 */
export function sanitizePath(path: string): string {
  return path.split(/[?#]/)[0].slice(0, MAX_PATH_LEN)
}

/** How close two same-path pageviews must be to count as a double-fire. */
export const PAGEVIEW_DEDUPE_MS = 1000

export type LastPageview = { path: string; ts: number } | null

/**
 * True when a pageview should be SKIPPED because it duplicates the immediately
 * preceding one (same path within PAGEVIEW_DEDUPE_MS). Kills the ~1% of
 * double-fires (effect re-invokes / quick remounts) that inflated view counts,
 * while a genuine reload or re-visit (beyond the window, or with a different
 * path in between) still records. Pure so it's unit-tested without a browser.
 */
export function isDuplicatePageview(
  prev: LastPageview,
  path: string,
  nowMs: number,
  windowMs: number = PAGEVIEW_DEDUPE_MS,
): boolean {
  return prev !== null && prev.path === path && nowMs - prev.ts < windowMs
}

/** Validate + clean one raw client event. Returns null to drop it. */
export function cleanEvent(e: unknown): CleanEvent | null {
  if (!e || typeof e !== 'object') return null
  const r = e as { type?: unknown; path?: unknown; label?: unknown }
  if (typeof r.type !== 'string' || !USER_EVENT_TYPES.includes(r.type as UserEventType)) return null
  if (typeof r.path !== 'string' || r.path.length === 0) return null
  const path = sanitizePath(r.path)
  if (!path) return null
  const label =
    typeof r.label === 'string' && r.label.trim() ? r.label.trim().slice(0, MAX_LABEL_LEN) : null
  return { type: r.type as UserEventType, path, label }
}

/** Validate a batch: cap count, drop invalid entries. */
export function cleanBatch(events: unknown): CleanEvent[] {
  if (!Array.isArray(events)) return []
  return events
    .slice(0, MAX_EVENTS_PER_BATCH)
    .map(cleanEvent)
    .filter((e): e is CleanEvent => e !== null)
}

/** Clamp a client-supplied anon id (used for pre-auth attribution). */
export function cleanAnonId(anonId: unknown): string | null {
  return typeof anonId === 'string' && anonId.trim()
    ? anonId.trim().slice(0, MAX_ANON_ID_LEN)
    : null
}
